/**
 * Split a live rep's message into text and link runs.
 *
 * Rep messages are NOT markdown — a rep typing "#1 pick" must not get a heading,
 * "235/60*19" must not italicize — so they bypass the ReactMarkdown path the AI's
 * messages use. That path is also what made bare URLs clickable, so until 8/20
 * a rep's link reached the customer as plain text to copy/paste (the sales team
 * reported it). This tokenizer recognizes exactly two shapes and nothing else:
 *
 *   - `[label](https://...)` — what the bridge produces from LivePerson's rich-text
 *     hyperlink button ("CLICK HERE"), see backend rep_text.py
 *   - bare `https://...` / `http://...` / `www.` URLs
 *   - a bare domain a rep pasted with no scheme (`tirerack.com`, `tirerack.com/tires/...`),
 *     restricted to a TLD allowlist so ordinary text ("index.jsp", "Node.js", "3.5",
 *     "e.g.") and email domains ("me@x.com") are left alone (requested by Miles 8/21)
 *
 * Everything else is returned verbatim as text.
 */
export type LinkToken =
  | { kind: 'text'; text: string }
  | { kind: 'link'; text: string; href: string };

// Markdown link first (so its URL isn't re-matched as bare), then bare URL.
// The labeled-link URL allows one level of balanced parens ("Foo_(bar)") so the
// link's own closing paren is still found.
const TOKEN_RE =
  /\[([^\]\n]+)\]\((https?:\/\/(?:[^\s()]|\([^\s()]*\))+)\)|(?:https?:\/\/|www\.)[^\s<>"'\]]+/g;

// Punctuation a sentence hangs on a URL that is almost never part of it.
const TRAILING_PUNCT = /[.,;:!?'"]+$/;

/** Trim sentence punctuation and an unbalanced closing paren off a bare URL. */
function trimUrl(raw: string): { url: string; rest: string } {
  let url = raw;
  let rest = '';
  const m = url.match(TRAILING_PUNCT);
  if (m) {
    rest = m[0];
    url = url.slice(0, -rest.length);
  }
  // "(see https://x.com/a)" — the closing paren belongs to the sentence unless
  // the URL itself opened one (wikipedia-style "Foo_(bar)").
  while (url.endsWith(')')) {
    const opens = (url.match(/\(/g) || []).length;
    const closes = (url.match(/\)/g) || []).length;
    if (closes <= opens) break;
    url = url.slice(0, -1);
    rest = ')' + rest;
  }
  return { url, rest };
}

// A bare domain with no scheme ("tirerack.com", "tirerack.com/tires/x.jsp?y=1"). The TLD is
// allowlisted so we don't turn "index.jsp", "Node.js", "235/60R18", "e.g." or "etc." into
// links; the allowlist gates only the domain's own TLD, never the path after the first "/".
const BARE_DOMAIN_RE =
  /(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|edu|gov|io|co|info|biz|us|ca|uk|app|dev|shop|store|online|me|tv)(?:\/[^\s<>"'\)\]]*)?/gi;

/** Linkify bare domains inside a plain-text run (never touches already-matched URLs, which
 *  arrive as link tokens). Skips email domains and anything glued to a preceding token. */
function linkifyBareDomains(text: string): LinkToken[] {
  const out: LinkToken[] = [];
  let last = 0;
  for (const m of text.matchAll(BARE_DOMAIN_RE)) {
    const start = m.index ?? 0;
    // "me@x.com" (email), "foo.tirerack.com" already inside a longer token, or a mid-path
    // "/x.com" — a preceding @, word char, dot or slash means this isn't a fresh URL.
    const prev = start > 0 ? text[start - 1] : '';
    if (/[@\w./]/.test(prev)) continue;
    if (start > last) out.push({ kind: 'text', text: text.slice(last, start) });
    const { url, rest } = trimUrl(m[0]);
    out.push({ kind: 'link', text: url, href: `https://${url}` });
    if (rest) out.push({ kind: 'text', text: rest });
    last = start + m[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out.length ? out : [{ kind: 'text', text }];
}

export function tokenizeLinks(text: string): LinkToken[] {
  if (!text) return [];
  const out: LinkToken[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN_RE)) {
    const start = m.index ?? 0;
    if (start > last) out.push({ kind: 'text', text: text.slice(last, start) });
    if (m[1] !== undefined && m[2] !== undefined) {
      out.push({ kind: 'link', text: m[1], href: m[2] });
      last = start + m[0].length;
      continue;
    }
    const { url, rest } = trimUrl(m[0]);
    const href = url.startsWith('www.') ? `https://${url}` : url;
    out.push({ kind: 'link', text: url, href });
    if (rest) out.push({ kind: 'text', text: rest });
    last = start + m[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  // Second pass: linkify bare domains inside the remaining text runs (scheme/www URLs and
  // labeled links are already link tokens, so they're untouched).
  const expanded = out.flatMap((t) => (t.kind === 'text' ? linkifyBareDomains(t.text) : [t]));
  // Merge adjacent text runs so callers get a minimal token list.
  return expanded.reduce<LinkToken[]>((acc, t) => {
    const prev = acc[acc.length - 1];
    if (t.kind === 'text' && prev && prev.kind === 'text') {
      prev.text += t.text;
    } else {
      acc.push({ ...t });
    }
    return acc;
  }, []);
}
